"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.lacrosseField = void 0;
/**
 * Lacrosse: one half of a field, centered on the goal the trainer attacks
 * (experiences ADR-045). The goal line is y = 0, the field of play runs to
 * the midfield line at negative y, and the ground behind the goal to the end
 * line at positive y.
 *
 * The stage's features are the protocol's; the women's field is its stage
 * exactly. The arena is the ground out to the stage's edges and the goal
 * mouth, which ends a flight that reaches it: no net, no walls, nothing past
 * the end line or midfield.
 */
const models_1 = require("../../models");
const freeze_1 = require("../freeze");
const features_1 = require("./features");
/**
 * NCAA rulebooks, Rule 1, as read in webby-app's util/lacrosse/geometry.ts.
 * Meters where the rulebook writes meters, yards and feet where it writes
 * those.
 */
const FIELDS = {
    // 120 x 70 yd (the largest the rules allow, as physics' volume and the
    // protocol's stage have it), the goals 100 yd apart, a 2.6 m goal circle,
    // the restraining line 30 yd up field.
    ncaaWomens: {
        width: 70 * models_1.metersPerYard,
        toMidfield: 50 * models_1.metersPerYard,
        behindGoal: 10 * models_1.metersPerYard,
        creaseRadius: 2.6,
        toRestrainingLine: 30 * models_1.metersPerYard,
    },
    // 110 x 60 yd, the goal line 15 yd off the end line, a 9 ft crease, the
    // restraining line 20 yd out.
    ncaaMens: {
        width: 60 * models_1.metersPerYard,
        toMidfield: 40 * models_1.metersPerYard,
        behindGoal: 15 * models_1.metersPerYard,
        creaseRadius: 3 * models_1.metersPerYard,
        toRestrainingLine: 20 * models_1.metersPerYard,
    },
};
/** A 6 x 6 ft cage in both codes; its net meets the ground 7 ft behind the center of the goal line. */
const GOAL = { width: 6 * models_1.metersPerFoot, height: 6 * models_1.metersPerFoot, depth: 7 * models_1.metersPerFoot };
/**
 * The protocol's lacrosse features. The goal is a prism, the cage's
 * footprint up to the crossbar; the crease is a circle. The women's 8 m arc
 * and 12 m fan wait for an `arc` kind.
 */
function fieldFeatures(f) {
    const hw = f.width / 2;
    const gw = GOAL.width / 2;
    return {
        center: (0, features_1.point)(0, 0),
        goal_line: (0, features_1.segment)(-gw, 0, gw, 0),
        goal_line_extended: (0, features_1.segment)(-hw, 0, hw, 0),
        goal: {
            kind: "polygon",
            points: [
                { x: -gw, y: 0 },
                { x: gw, y: 0 },
                { x: 0, y: GOAL.depth },
            ],
            z: { min: 0, max: GOAL.height },
        },
        crease: { kind: "circle", at: { x: 0, y: 0 }, radius: f.creaseRadius },
        restraining_line: (0, features_1.segment)(-hw, -f.toRestrainingLine, hw, -f.toRestrainingLine),
        midfield_line: (0, features_1.segment)(-hw, -f.toMidfield, hw, -f.toMidfield),
        end_line: (0, features_1.segment)(-hw, f.behindGoal, hw, f.behindGoal),
        sideline_left: (0, features_1.segment)(-hw, -f.toMidfield, -hw, f.behindGoal),
        sideline_right: (0, features_1.segment)(hw, -f.toMidfield, hw, f.behindGoal),
    };
}
/** The goal mouth, stopping what the trainer's side sends into it. */
const goalMouth = {
    id: "goal",
    kind: "goal",
    from: { x: -GOAL.width / 2, y: 0 },
    to: { x: GOAL.width / 2, y: 0 },
    bottom: 0,
    top: GOAL.height,
    faces: "right",
    response: { kind: "absorb" },
};
/**
 * The stage's own frame (ADR-045 asks vision to adopt it): origin at the
 * goal-line midpoint, +y through the goal, millimeters. Reference points are
 * the cage and the crease, not v1's placeholder tennis COURTPOINTS. Vision
 * has no lacrosse model yet, so this is a proposal it may revise.
 */
function goalFrame(f) {
    const gw = GOAL.width / 2;
    const r = f.creaseRadius;
    return {
        localization: { origin: (0, features_1.vec3)(0, 0, 0), flipY: false, unitsPerMeter: 1000.0 },
        referencePoints: {
            goal_line_center: (0, features_1.vec3)(0, 0, 0),
            goal_post_left_bottom: (0, features_1.vec3)(-gw, 0, 0),
            goal_post_left_top: (0, features_1.vec3)(-gw, 0, GOAL.height),
            goal_post_right_bottom: (0, features_1.vec3)(gw, 0, 0),
            goal_post_right_top: (0, features_1.vec3)(gw, 0, GOAL.height),
            crossbar_center: (0, features_1.vec3)(0, 0, GOAL.height),
            goal_back: (0, features_1.vec3)(0, GOAL.depth, 0),
            crease_left: (0, features_1.vec3)(-r, 0, 0),
            crease_right: (0, features_1.vec3)(r, 0, 0),
            crease_front: (0, features_1.vec3)(0, -r, 0),
        },
    };
}
function buildField(field) {
    const spec = FIELDS[field];
    const hw = spec.width / 2;
    const extents = { x: [-hw, hw], y: [-spec.toMidfield, spec.behindGoal] };
    return {
        id: `lacrosse/${field}`,
        sport: "LACROSSE",
        stage: { extents, features: fieldFeatures(spec) },
        arena: {
            bounds: extents,
            ground: { restitution: models_1.DefaultPhysicalConstants.courtRestCoeff },
            panels: [goalMouth],
        },
        frame: goalFrame(spec),
    };
}
const VENUES = (0, freeze_1.deepFreeze)({
    ncaaWomens: buildField("ncaaWomens"),
    ncaaMens: buildField("ncaaMens"),
});
/** A lacrosse half-field, in the goal-centered frame. `ncaaWomens` is the sport's default. */
const lacrosseField = (field) => VENUES[field];
exports.lacrosseField = lacrosseField;
